<script setup lang="ts">
import refundRequestsHttpService from '@/services/http/refundRequestsHttpService.ts'
import { PaymentMethods } from '@/constants.ts'
import { Roles } from '@/constants.ts'
import { computed, ref, onMounted, reactive } from 'vue'
import { useToast } from 'primevue/usetoast'
import type { RefundRequestModel } from '@/services/http/httpResponses.ts'
import { RefundRequestStatuses, type RefundRequestStatus, type Id } from '@raildev/shared-utils'

definePage({
  meta: {
    allowedRoles: [Roles.Admin],
  },
})

const toast = useToast()

type FilterParams = {
  id: Id | null
  status: RefundRequestStatus | null
  userId: Id | null
}

const filterParams = reactive<FilterParams>({
  id: null,
  status: null,
  userId: null,
})

const refundStatuses = Object.entries(RefundRequestStatuses)
  .filter(([_, value]) => value != RefundRequestStatuses.None)
  .map(([label, value]) => ({ label, value }))

const refundRequests = ref<RefundRequestModel[]>([])

const refreshRefundRequests = async () => {
  const resp = await refundRequestsHttpService.getRefundRequests({
    id: filterParams.id,
    status: filterParams.status,
    userId: filterParams.userId,
  })
  refundRequests.value = resp.requests
}

const getPaymentMethodName = (m?: number | null) => {
  switch (m) {
    case PaymentMethods.TipTop:
      return 'TipTop (карта)'
    case PaymentMethods.KaspiRemotePhone:
      return 'Kaspi (удалённый счёт)'
    default:
      return ''
  }
}

const getStatus = (status: RefundRequestStatus) => {
  const code = refundStatuses.find((s) => s.value === status)?.label ?? String(status)
  switch (code) {
    case 'Pending':
      return 'В ожидании'
    case 'Processing':
      return 'В работе'
    case 'Completed':
      return 'Одобрено'
    case 'Rejected':
      return 'Отказано'
    default:
      return code
  }
}

const getStatusSeverity = (s: RefundRequestStatus) => {
  switch (s) {
    case RefundRequestStatuses.Completed:
      return 'success'
    case RefundRequestStatuses.Rejected:
      return 'danger'
    case RefundRequestStatuses.Processing:
      return 'warn'
    case RefundRequestStatuses.Pending:
    default:
      return 'info'
  }
}

const canTakeInWork = (r: RefundRequestModel) => r.status === RefundRequestStatuses.Pending
const canDecide = (r: RefundRequestModel) => r.status === RefundRequestStatuses.Processing

const actionInProgress = ref<Id | null>(null)
const isActionLoading = (id: Id) => actionInProgress.value === id

const updateStatus = async (
  req: RefundRequestModel,
  status: RefundRequestStatus,
  label: string,
) => {
  try {
    actionInProgress.value = req.id
    await refundRequestsHttpService.updateRefundRequestStatus(req.id, status)
    toast.add({
      severity: 'success',
      summary: 'Успех',
      detail: `Статус: ${label}`,
      life: 3000,
    })
    await refreshRefundRequests()
  } catch {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось обновить статус',
      life: 3000,
    })
  } finally {
    actionInProgress.value = null
  }
}

const showRejectDialog = ref(false)
const rejectForm = reactive<{
  requestId: Id | null
  comment: string
}>({
  requestId: null,
  comment: '',
})

const isRejectButtonDisabled = computed<boolean>(() => {
  return rejectForm.requestId === null || rejectForm.comment.trim().length < 10
})

const openRejectDialog = (req: RefundRequestModel) => {
  rejectForm.requestId = req.id
  rejectForm.comment = ''
  showRejectDialog.value = true
}

const closeRejectDialog = () => {
  showRejectDialog.value = false
  rejectForm.requestId = null
  rejectForm.comment = ''
}

const submitReject = async () => {
  if (rejectForm.requestId == null) {
    return
  }
  const comment = rejectForm.comment.trim()
  if (comment.length < 10) {
    return
  }
  try {
    actionInProgress.value = rejectForm.requestId
    await refundRequestsHttpService.updateRefundRequestStatus(
      rejectForm.requestId,
      RefundRequestStatuses.Rejected,
      comment,
    )
    toast.add({
      severity: 'success',
      summary: 'Успех',
      detail: 'Статус: Отказано',
      life: 3000,
    })
    closeRejectDialog()
    await refreshRefundRequests()
  } catch {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось обновить статус',
      life: 3000,
    })
  } finally {
    actionInProgress.value = null
  }
}

const showActionsColumn = computed(() => true)

onMounted(async () => {
  const urlParams = new URLSearchParams(window.location.search)
  const id = urlParams.get('id')
  if (id) {
    filterParams.id = id as unknown as Id
  }

  await refreshRefundRequests()
})
</script>

<template>
  <div class="flex flex-col gap-4 w-full">
    <Toast />

    <div class="flex flex-row justify-between items-center w-full">
      <div class="flex gap-3">
        <InputText v-model="filterParams.id" placeholder="ID заявки" />
        <Select
          :options="refundStatuses"
          v-model="filterParams.status"
          option-label="label"
          option-value="value"
          fluid
          show-clear
          placeholder="Статус"
        />
        <InputText v-model="filterParams.userId" placeholder="User ID" />
      </div>

      <Button label="Применить" @click="refreshRefundRequests()" />
    </div>

    <DataTable ref="dt" :value="refundRequests" dataKey="id" class="w-full">
      <template #header>
        <div class="flex flex-wrap gap-2 items-center justify-between"></div>
      </template>

      <Column header="Статус" style="min-width: 12rem">
        <template #body="slotProps">
          <Tag
            :value="getStatus(slotProps.data.status)"
            :severity="getStatusSeverity(slotProps.data.status)"
          />
        </template>
      </Column>

      <Column header="Дата создания" style="min-width: 12rem">
        <template #body="slotProps">
          {{ slotProps.data.createdDate?.toLocaleString('ru-RU') }}
        </template>
      </Column>

      <Column header="Пользователь" style="min-width: 12rem">
        <template #body="slotProps">
          {{ slotProps.data.user?.name ?? '-' }}
        </template>
      </Column>

      <Column header="Способ" style="min-width: 12rem">
        <template #body="slotProps">
          {{ getPaymentMethodName(slotProps.data.paymentMethod) }}
        </template>
      </Column>

      <Column header="Телефон (Kaspi)" style="min-width: 12rem">
        <template #body="slotProps">
          {{ slotProps.data.customerPhone ?? '-' }}
        </template>
      </Column>

      <Column header="Причина" style="min-width: 18rem">
        <template #body="slotProps">
          <span class="break-words">{{ slotProps.data.reason }}</span>
        </template>
      </Column>

      <Column header="Комментарий админа" style="min-width: 18rem">
        <template #body="slotProps">
          <span class="break-words">{{ slotProps.data.adminComment ?? '-' }}</span>
        </template>
      </Column>

      <template #empty>
        <div class="flex flex-col items-center gap-2 py-6 text-gray-400">
          <i class="pi pi-inbox text-4xl" />
          <span>Данные отсутствуют</span>
        </div>
      </template>
      <Column v-if="showActionsColumn" header="Действия" style="min-width: 18rem">
        <template #body="slotProps">
          <div class="flex flex-col gap-2 w-full max-w-64">
            <Button
              class="w-full"
              size="small"
              label="Взять в работу"
              severity="warning"
              :disabled="!canTakeInWork(slotProps.data) || isActionLoading(slotProps.data.id)"
              @click="updateStatus(slotProps.data, RefundRequestStatuses.Processing, 'В работе')"
            />

            <Button
              class="w-full"
              size="small"
              label="Одобрить"
              severity="success"
              :disabled="!canDecide(slotProps.data) || isActionLoading(slotProps.data.id)"
              @click="updateStatus(slotProps.data, RefundRequestStatuses.Completed, 'Одобрено')"
            />

            <Button
              class="w-full"
              size="small"
              label="Отказать"
              severity="danger"
              :disabled="!canDecide(slotProps.data) || isActionLoading(slotProps.data.id)"
              @click="openRejectDialog(slotProps.data)"
            />
          </div>
        </template>
      </Column>
    </DataTable>

    <Dialog
      v-model:visible="showRejectDialog"
      header="Причина отказа"
      :modal="true"
      :class="'w-[35vw]'"
      :closable="false"
      :pt="{
        header: 'justify-center',
        footer: 'border-t-[1px] border-slate-200 p-0',
        content: 'p-0',
      }"
    >
      <div class="flex flex-col gap-4 border-b-[1px] border-slate-200 px-[20px] pb-[20px]">
        <div class="flex flex-col gap-2">
          <label class="block" for="rejectComment">Комментарий администратора</label>
          <Textarea
            id="rejectComment"
            v-model="rejectForm.comment"
            rows="6"
            :auto-resize="false"
            class="w-full"
            placeholder="Укажи причину отказа"
          />
        </div>
      </div>

      <template #footer>
        <div class="flex grow flex-col gap-4 px-[20px] py-[20px]">
          <div class="flex gap-4">
            <Button
              class="basis-1/2"
              label="Отмена"
              severity="secondary"
              @click="closeRejectDialog"
            />
            <Button
              class="basis-1/2"
              label="Отказать"
              severity="danger"
              :disabled="isRejectButtonDisabled"
              :loading="rejectForm.requestId != null && isActionLoading(rejectForm.requestId)"
              @click="submitReject"
            />
          </div>
        </div>
      </template>
    </Dialog>
  </div>
</template>

<style scoped></style>
